import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import type { Aggregate } from 'src/components/followUp/common/types';

type UseExportOptionsProps = {
  data: Aggregate | null;
  comparisonData?: Aggregate | null;
  percentage: number;
  comparisonPercentage?: number;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapDataToRows = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (data: Aggregate | null, percentage: number): Row[] => [
      [
        { value: t('statistics.responseRate'), fontWeight: 'bold' },
        { value: `${Math.round(percentage * 100)}%`, align: 'right' },
      ],
      [
        { value: t('statistics.invitees'), fontWeight: 'bold' },
        { value: data?.totalObjects ?? 0 },
      ],
      [
        { value: t('statistics.respondents'), fontWeight: 'bold' },
        { value: data?.value ?? 0 },
      ],
    ],
    [t],
  );
};

export const useExportOptions = ({
  data,
  comparisonData,
  percentage,
  comparisonPercentage,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const dataMapper = useMapDataToRows();

  return useMemo(() => {
    const rows = dataMapper(data, percentage);
    const comparisonRows =
      comparisonData && comparisonPercentage !== undefined
        ? dataMapper(comparisonData, comparisonPercentage ?? 0)
        : undefined;

    return {
      widgetName: widgetName,
      rows,
      queryFilters,
      comparisonRows,
    };
  }, [
    data,
    comparisonData,
    percentage,
    comparisonPercentage,
    widgetName,
    queryFilters,
    dataMapper,
  ]);
};

interface UseSpeedometerCalculationsProps {
  data?: Aggregate;
  comparisonData?: Aggregate;
  isFetching?: boolean;
  errorMsg?: string;
}

export const useSpeedometerCalculations = ({
  data,
  comparisonData,
  isFetching = false,
  errorMsg,
}: UseSpeedometerCalculationsProps) => {
  const absoluteValue = useMemo(() => {
    if (isFetching || errorMsg) return 0;
    return getRelativeValue(data?.value, data?.totalObjects);
  }, [data, isFetching, errorMsg]);

  const percentage = absoluteValue / 100;

  const comparisonPercentage = useMemo(() => {
    if (!comparisonData) return undefined;
    return (
      getRelativeValue(comparisonData.value, comparisonData.totalObjects) / 100
    );
  }, [comparisonData]);

  const percentageDifference = useMemo(() => {
    if (comparisonPercentage === undefined) return undefined;
    const difference = Math.round((percentage - comparisonPercentage) * 100);
    return `${difference >= 0 ? '+' : ''}${difference}%`;
  }, [percentage, comparisonPercentage]);

  const absoluteDifference = useMemo(() => {
    if (!data || !comparisonData) return undefined;

    const totalObjects = data.totalObjects - comparisonData.totalObjects;
    const value = data.value - comparisonData.value;

    return {
      totalObjects: `${totalObjects >= 0 ? '+' : ''}${totalObjects}`,
      value: `${value >= 0 ? '+' : ''}${value}`,
    };
  }, [data, comparisonData]);

  return {
    absoluteValue,
    percentage,
    comparisonPercentage,
    percentageDifference,
    absoluteDifference,
  };
};
